import React from 'react';

import { RouteContext } from 'src/context/routeContext';
import { DEFAULT_APPS } from 'src/hooks/useApps';
import { useGetInstallsQuery } from 'src/services/api/applicationsApi';

import { ExtensionViewTypeValue } from '../../ExtensionsPage/ExtensionViewType';

import { ModuleFormValuesType, ModuleTypes } from './appSetupTypes';

export interface EditModulePageQueryParams {
  moduleId: string;
  moduleType: string;
  preset?: string; // present if this is a preset extension
  appId?: string; // exist if an app is internal app
}
/**
 * This hook is used to get the editing module data from module settings (if it is core module)
 * or from the extensions settings (if it is an extension module).
 */
export const useEditingApp = (): ModuleFormValuesType | null => {
  const { query } = React.useContext(RouteContext);
  const { moduleId, moduleType } = query;
  const { data: { moduleSettings, extensionsSettings } = {} } =
    useGetInstallsQuery();

  const editingModule = React.useMemo((): ModuleFormValuesType | null => {
    if (!moduleId) return null;

    const module = (moduleSettings ?? []).find((mod) => mod.id === moduleId);

    // when it is an extension module, get the extension config and return it
    if (moduleType !== ModuleTypes.Core) {
      const extensionModuleConfig = Object.entries(
        extensionsSettings || {},
      ).find(([extId]) => extId === moduleId);
      if (extensionModuleConfig) {
        const [_, extConfig] = extensionModuleConfig;
        let extensionType = extConfig.type as ModuleTypes;
        if (
          extConfig.type === ModuleTypes.Global &&
          extConfig.isAuthenticated
        ) {
          extensionType = ModuleTypes.Authenticated;
        }

        return {
          ...(module as ModuleFormValuesType),
          type: extensionType,
          label: extConfig.name,
          icon: extConfig.icon,
          embedCode: extConfig.embedCode,
          embedLink: extConfig.embedLink,
          autoSizeEmbed: extConfig.autoSizeEmbed,
          embedlyData: extConfig.embedlyData,
          content: extConfig.embedCode || extConfig.embedLink,
          viewType: extConfig.embedLink
            ? ExtensionViewTypeValue.LINK
            : ExtensionViewTypeValue.EMBED,
          queryParams: extConfig.queryParams,
        };
      }
    }

    // when core module is not in the moduleSettings store yet
    if (!module) {
      const defaultModule = DEFAULT_APPS.find((mod) => mod.id === moduleId);
      // FIXME: not taking the time to strongly type this, but we should
      return {
        ...defaultModule,
        label: defaultModule?.title,
        icon: defaultModule?.iconName,
      } as ModuleFormValuesType;
    }

    return module as ModuleFormValuesType;
  }, [moduleId, moduleSettings, moduleType, extensionsSettings]);

  return editingModule;
};
